import type { AxiosError } from "axios";
import Axios from "axios";
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import styled, { ThemeContext } from "styled-components";
import { useAuthContext } from "../../../../../components/Auth";
import {
  ListGridToggleButton,
  PrimaryButtonLargeV2,
  PrimaryButtonMediumV2,
  TertiaryButtonMediumV2,
  TertiaryTextButtonSmall,
} from "../../../../../components/Buttons/Buttons";
import {
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  InfoIcon,
  LinkIcon,
  LoadingIcon,
  LockIcon,
  NonVisibleIcon,
  QuoteIcon,
  ShareIcon,
  UnLockIcon,
  VisibleIcon,
} from "../../../../../components/Icons/Icons";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { Loading, Table } from "../../../../../components/Table/Table";
import { endpoints } from "../../../../../endpoints";
import type {
  AssetContainerAssociationHandler,
  AssetContainerType,
} from "../../../../../util/assetContainer.types";
import type {
  DataMutate,
  IPagination,
  Language,
  Permission,
  WithPagination,
} from "../../../../../types/types";
import { isChipTypeArray } from "../../../../../types/types";
import type {
  Assets,
  GridItemProps,
  PIMProduct,
  SupportedAssetAccessibility,
  SupportedAssetCategoryType,
} from "../../../../../types/types.PIM";
import {
  formatDate,
  isAxiosError,
  TablePlaceholder,
  TablePlaceholderWrapper,
  useStoreState,
  useSupportedLanguages,
} from "../../../../../util/util";
import { EditAssetForm } from "../EditAssetForm";
import {
  getActiveTemplateGeneratedAssets,
  useTemplateGeneratedAssets,
} from "../../hooks/useTemplateGeneratedAssets";
import { AddAssetsForm } from "./AddAssetsForm";
import { RemoveAssetFromProduct } from "./RemoveAssetFromProduct";

import type { Getter, Row as TableRow } from "@tanstack/react-table";
import type { Moment } from "moment";
import { stringify } from "querystring";
import type { Row as RowType } from "react-table";
import useSWR from "swr";
import type { DecodedValueMap, QueryParamConfigMap } from "use-query-params";
import { ArrayParam, ObjectParam, useQueryParams } from "use-query-params";
import type { ChipType } from "../../../../../components/Chips/Chips";
import type { DateRangeStr } from "../../../../../components/DateFilter/date_filter.utils";
import { get_preselected_date_range } from "../../../../../components/DateFilter/date_filter.utils";
import { DateFilter } from "../../../../../components/DateFilter/DateFilter";
import { FilterBy } from "../../../../../components/FilterBy/FilterBy";
import { chipsToStringArray } from "../../../../../components/Filters/filterutils";
import { Row } from "../../../../../components/Layout/Layout";
import { SlideOut } from "../../../../../components/SlideOut/SlideOut";
import { TableWithBulkAction } from "../../../../../components/Table/TableWithBulkAction";
import { H3, SmallText } from "../../../../../components/Typography/Typography";
import { WithPermission } from "../../../../../components/WithPermission/WithPermission";
import { Flex, Flex1 } from "../../../../../layout/FormLayout";
import { getCountryOption } from "../../../../../util/location";
import { useCountriesList } from "../../../../../util/Locations";
import { GridAssetView } from "../../../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/GridAssetView/GridAssetView";
import { GridAssetViewWithBulkAction } from "../../../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/GridAssetView/GridAssetViewWithBulkAction";
import {
  convertToSlug,
  getLanguageOptionFromSupportedLanguage,
} from "../../../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/AssetsUtil";
import {
  BulkActionOnAssets,
  fetchSelectedAssets,
} from "../BulkActionOnAssets/BulkActionOnAssets";
import { EditOrGenerateProductDocument } from "../EditOrGenerateProductDocuments/EditOrGenerateProductDocument";
import {
  ACCESSIBILITY_TYPES,
  accessibilityRequestData,
  AssetNameIcon,
  disableAccessibilitySubmit,
  DocPortfolioName,
  getCustomersExistingData,
  getRolesExistingData,
  getUsersExistingData,
  NewAssetFormSubmitValues,
} from "../util";
import { DocumentPreview } from "../../../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/DocumentPreview";
import { Modal } from "../../../../../components/Modal/Modal";
import { RadioButton } from "../../../../../components/RadioButton/RadioButton";
import { ErrorPlaceholder } from "../../../../../components/Error";
import { PlusIcon } from "../../../../../components/Icons/Icons";
import { SearchBar } from "../../../../../components/SearchBar/SearchBar";
import { useDebounce } from "../../../../../util/hooks";
import {
  getAssetAssociateEndpoint,
  getAssetListEndpoint,
} from "../../../../../util/assetEndpoints";
import {
  DropdownItem,
  StyledDropdownMenu,
} from "../../../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/AssetsList/AssetsList";
import type { IShareAssetsData } from "../../../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/AssetsList/ShareAsset";
import { ShareAsset } from "../../../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/AssetsList/ShareAsset";
import { createPortal } from "react-dom";
import type { TableRefProps } from "../../../../../components/Table/utils";
import { DocumentAccessibility } from "../../../../../components/DocumentAccessibility/DocumentAccessibility";
import { useForm, useWatch } from "react-hook-form";
import { spacingTokens } from "../../../../../designTokens";
import { PROFILE_DETAIL_CLASSES } from "../../../../../layout/shared/ProfileDetailLayout";
import { AccordionCard } from "../../../../../components/AccordionCard/AccordionCard";
import { ChangeRequestFormModal } from "../../../../../components/ChangeRequestForm/ChangeRequestModal";
import {
  ChangeRequestFormValues,
  submitChangeRequest,
} from "../../../../../components/ChangeRequestForm/changerequest.util";
import {
  ENTITY_MODIFY_PERMISSIONS,
  EntityType,
} from "../../../../../types/types.MDM";
import { getProductTypePermissionSuffix } from "../../ProductOverview/product_overview.utils";

const TableContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_3xl")};
  & div[class*="TableWrapper"] {
    overflow: visible;
    td {
      overflow: visible;
    }
  }
`;

export const TableFiltersWrapper = styled(Flex1)<{ has_padding: boolean }>`
  border-left: ${({ theme }) =>
    `${theme.resolve("border_3xl")} solid ${theme.secondaryBorder}`};
  border-top: ${({ theme }) =>
    `${theme.resolve("border_md")} solid ${theme.secondaryBorder}`};
  border-right: ${({ theme }) =>
    `${theme.resolve("border_md")} solid ${theme.secondaryBorder}`};
  padding: ${({ has_padding }) => (has_padding ? "5px 10px" : "0")};
`;

export const ScrollTableWrapper = styled.div<{
  maxHeight?: string;
  ref?:
    | React.RefObject<HTMLDivElement>
    | ((instance: HTMLDivElement | null) => void)
    | null
    | undefined;
}>`
  max-height: ${({ maxHeight }) => (maxHeight ? maxHeight : "348px")};
  position: relative;
  overflow: auto;
  border: ${({ theme }) =>
    `${theme.resolve("border_md")} solid ${theme.resolve(
      "color_border_lighter"
    )}`};
  border-left: 0;
  table th {
    border-top: 0;
    position: sticky;
    position: -webkit-sticky;
    z-index: 2;
    top: 0;
  }
  table {
    border: 0;
  }

  table thead td:last-child {
    border-bottom: 0;
  }
  ${TablePlaceholderWrapper} {
    border-bottom: 0;
    border-right: 0;
  }
  ${Loading} {
    padding: ${({ theme }) => theme.resolve("spacing_2xl")} 0;
    border-left: ${({ theme }) =>
      `${theme.resolve("border_3xl")} solid ${theme.secondaryBorder}`};
  }
`;

const ScrollGridWrapper = styled.div`
  max-height: 670px;
  position: relative;
  overflow: auto;
`;

const ModalWrapper = styled.div`
  width: 100%;
  height: 162px;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_4xl")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  padding: ${({ theme }) => theme.resolve("spacing_xs")};
  border: solid ${({ theme }) => theme.resolve("border_md")}
    ${({ theme }) => theme.errorColor};
  background-color: ${({ theme }) => theme.destructiveButtonHover};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

type TableProduct = {
  id: string | boolean;
  name: string;
  expiration_date: string;
  issue_date: string;
  country: string;
  region: string;
  document_type: string;
  asset_type: string;
  category: {
    id: string;
    name: SupportedAssetCategoryType;
  };
  host: {
    id: string;
    name: string;
  };
  language: string;
};

type TableData = (TableProduct & {
  info: {
    modified_at: string;
    modified_by: string;
    number_of_products: number;
    number_of_profiles: number;
    renewal_date: string;
  };
  country_region: string;
  visibility: SupportedAssetAccessibility;
  canSelect: boolean;
  rowTooltipMessage?: string | JSX.Element;
} & {
    [Prop in keyof Assets]+?: string | number | boolean | ChipType | object;
  })[];

type DateRange = {
  startDate: Moment | null;
  endDate: Moment | null;
};

const IconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: center;
  column-gap: ${({ theme }) => theme.resolve("spacing_md")};
`;

const BtnWrapper = styled.button`
  border: none;
  background-color: transparent;
  cursor: pointer;
  :disabled {
    cursor: not-allowed;
  }
`;

/** Overrides AccordionCard Actions `button { margin-right }` so the toggle stays flush. */
const ListGridToggleWrapper = styled.div`
  && button {
    margin-right: 0;
    padding-right: ${({ theme }) => theme.resolve("spacing_lg")};
  }
`;

export const DocumentsOrDigitalMediaTable = ({
  product,
  title,
  fetchProductData,
  replaceProductId = () => {},
  assetContainerType = "product",
  assetContainerId,
  onAssociateAssets,
  onRemoveAssociatedAsset,
  onAddNewAssetData,
  allowGenerateDocuments = true,
  useSignedAssetDownloadUrl = false,
  removeConfirmationBody,
  isOutsideLogin = false,
  isShowTableWithBulkAction = false,
  isShowGridAssetWithBulkAction = false,
  documentType,
  hideViewToggle = false,
  hideFilters = false,
  showSearch = false,
  hideAddButton = false,
  showAddButtonAtTop = false,
  uploadNewDocumentsOnly = false,
  hideHeader = false,
  hideShareIcon = false,
  hideAccessibility = false,
  allowUpload = false,
  enableSave = false,
  isTabActive,
}: {
  title: string;
  product: PIMProduct;
  fetchProductData: DataMutate<any>;
  replaceProductId?: (id: string) => void;
  assetContainerType?: AssetContainerType;
  assetContainerId?: string;
  onAssociateAssets?: AssetContainerAssociationHandler;
  onRemoveAssociatedAsset?: (assetId: string) => Promise<void>;
  onAddNewAssetData?: (data: NewAssetFormSubmitValues) => Promise<void>;
  allowGenerateDocuments?: boolean;
  useSignedAssetDownloadUrl?: boolean;
  removeConfirmationBody?: string;
  isOutsideLogin?: boolean;
  isShowTableWithBulkAction?: boolean;
  isShowGridAssetWithBulkAction?: boolean;
  documentType?: string;
  hideViewToggle?: boolean;
  hideFilters?: boolean;
  showSearch?: boolean;
  hideAddButton?: boolean;
  showAddButtonAtTop?: boolean;
  uploadNewDocumentsOnly?: boolean;
  hideHeader?: boolean;
  hideShareIcon?: boolean;
  hideAccessibility?: boolean;
  allowUpload?: boolean;
  enableSave?: boolean;
  isTabActive?: boolean;
}) => {
  const { t } = useTranslation();
  const theme = useContext(ThemeContext);
  const [assetData, setAssetData] = useState<Assets[]>([]);
  const [pagination, setPagination] = useState<IPagination>();
  const [offset, setOffset] = useState<number>(0);
  const [activeFilter, setActiveFilter] = useState<string>("");
  const [query, setQuery] = useQueryParams(
    title === "Documents"
      ? {
          document_country: ArrayParam,
          document_region: ArrayParam,
          document_type: ArrayParam,
          document_language: ArrayParam,
          document_category: ArrayParam,
          asset_type: ArrayParam,
          document_asset_host: ArrayParam,
          issue_date: ObjectParam,
          expiration_date: ObjectParam,
          renewal_date: ObjectParam,
        }
      : {
          media_country: ArrayParam,
          media_region: ArrayParam,
          media_document_type: ArrayParam,
          asset_type: ArrayParam,
          media_asset_host: ArrayParam,
          media_language: ArrayParam,
          media_category: ArrayParam,
          media_issue_date: ObjectParam,
          media_expiration_date: ObjectParam,
          media_renewal_date: ObjectParam,
        }
  );
  const countries = useCountriesList();
  const {
    tenant_id,
    is_publishing_product,
    storefront_id,
    slug,
    storefront_metadata: {
      enable_product_asset_search,
      enable_external_assets,
      enable_change_requests,
      enable_share_assets_via_email = true,
    },
    collapsed_nav,
  } = useStoreState();
  const { data: templateGeneratedAssets } = useTemplateGeneratedAssets(
    product?.product_schema?.template_id
  );
  const { hasPermission, roleIsGuest, roleIsSomeKindOfSeller } =
    useAuthContext();

  const resolvedAssetContainerId = assetContainerId ?? product.id;
  const isProductContainer = assetContainerType === "product";
  const productTypePermissionSuffix = getProductTypePermissionSuffix(
    product.pim_product_type
  );
  const productOrEntityPermission = isProductContainer
    ? (`modify_${productTypePermissionSuffix}` as Permission)
    : (ENTITY_MODIFY_PERMISSIONS[assetContainerType] as Permission);

  const isRestricted = useMemo(
    () => ["customer", "distributor", "supplier"].includes(assetContainerType),
    [assetContainerType]
  );

  const removeAssociationPermission = onRemoveAssociatedAsset
    ? "modify_assets"
    : "delete_assets";
  const assetsEndpointBase = getAssetListEndpoint(
    assetContainerType,
    tenant_id,
    resolvedAssetContainerId
  );
  const associateAssetsEndpoint = getAssetAssociateEndpoint(
    assetContainerType,
    tenant_id,
    resolvedAssetContainerId
  );

  const { notifySuccess, notifyError } = useContext(Notifications);

  const requestLimit = 20;
  const [searchQuery, setSearchQuery] = useState("");
  const [document_q, setDocumentQ] = useState("");
  const [media_q, setMediaQ] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const [showSlideOut, setSlideOut] =
    useState<
      "add" | "edit" | "delete" | "generate" | "change_request_form" | false
    >(false);

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [editRowData, setEditRowData] = useState<Assets | null>(null);
  const [editRowVisibility, setEditRowVisibility] =
    useState<SupportedAssetAccessibility | null>(null);
  const [deleteRowData, setDeleteRowData] = useState<Assets | null>(null);
  const [list_grid_toggle, set_list_grid_toggle] =
    useState<"list" | "grid">("list");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { supported_languages } = useSupportedLanguages();

  const product_document_ref = useRef<{ clear_poll_timeout: () => void }>(null);
  const resetSelectedRowsRef = useRef<TableRefProps>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const iconRefs = useRef<Record<string, HTMLElement | null>>({});
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const hasMountedRef = useRef(false);

  const closeSlideOut = useCallback(() => setSlideOut(false), []);
  const closeDeleteModal = () => setShowDeleteModal(false);

  const [showEditPopup, setShowEditPopup] = useState<boolean>(false);
  const [showEditConfirm, setShowEditConfirm] = useState<boolean>(false);
  const [tableData, setTableData] = useState<TableProduct[]>([]);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);

  const productStatus = useMemo(() => product.status, [product.status]);

  const [categoryTypes, setCategoryTypes] = useState<string[]>([]);

  const [assetTypes, setAssetTypes] = useState<string[]>([]);

  const [countryFilters, setCountryFilters] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);

  const [documentTypesFilters, setDocumentTypesFilters] = useState<string[]>(
    []
  );
  const [languagesFilters, setLanguagesFilters] = useState<string[]>([]);
  const [assetHostFilters, setAssetHostFilters] = useState<string[]>([]);

  const [selectedRows, setSelectedRows] = useState<string[] | "all">();
  const [selectedItems, setSelectedItems] = useState<Array<Assets>>([]);

  const [selectedAssetIdForSharing, setSelectedAssetIdForSharing] =
    useState<string | null>(null);

  const [openDropDownRowId, setOpenDropDownRowId] =
    useState<string | null>(null);

  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });

  const [showShareAssetModal, setShowShareAssetModal] = useState(false);
  const openShareAssetModal = () => setShowShareAssetModal(true);
  const closeShareAssetModal = () => setShowShareAssetModal(false);

  const [show_preview, set_show_preview] = useState(false);
  const [selected_asset_idx, set_selected_asset_idx] = useState(0);

  const assetQueryAsParam = useCallback(
    (params: DecodedValueMap<QueryParamConfigMap>) => {
      const result: DecodedValueMap<QueryParamConfigMap> = {};

      Object.keys(params).forEach((key) => {
        const val = params[key];

        if (val !== undefined && val !== null && val !== "") {
          if (key.includes("_category")) {
            result["asset_category"] = val;
          } else if (key.includes("asset_type")) {
            if (title !== "Documents") {
              result["asset_type"] = val;
            }
          } else if (key.includes("asset_host")) {
            result["asset_host"] = val;
          } else if (key.includes("document_type")) {
            result["document_type"] = val;
          } else if (key.includes("issue_date")) {
            const { startDate, endDate } = val ?? {
              startDate: null,
              endDate: null,
            };
            if (
              startDate &&
              startDate !== "undefined" &&
              startDate !== "null"
            ) {
              result["issue_date_start"] = startDate;
            }
            if (endDate && endDate !== "undefined" && endDate !== "null") {
              result["issue_date_end"] = endDate;
            }
          } else if (key.includes("expiration_date")) {
            const { startDate, endDate } = val ?? {
              startDate: null,
              endDate: null,
            };
            if (
              startDate &&
              startDate !== "undefined" &&
              startDate !== "null"
            ) {
              result["expiration_date_start"] = startDate;
            }
            if (endDate && endDate !== "undefined" && endDate !== "null") {
              result["expiration_date_end"] = endDate;
            }
          } else if (key.includes("renewal_date")) {
            const { startDate, endDate } = val ?? {
              startDate: null,
              endDate: null,
            };
            if (
              startDate &&
              startDate !== "undefined" &&
              startDate !== "null"
            ) {
              result["renewal_date_start"] = startDate;
            }
            if (endDate && endDate !== "undefined" && endDate !== "null") {
              result["renewal_date_end"] = endDate;
            }
          } else {
            result[key.split("_")[1]] = val;
          }
        }
      });

      return stringify(result) ? `&${stringify(result)}` : ``;
    },
    [title]
  );

  const get_document_search_param = () => {
    if (document_q && title === "Documents") {
      return `&q=${encodeURIComponent(document_q)}`;
    }
    if (media_q && title === "Digital Media") {
      return `&q=${encodeURIComponent(media_q)}`;
    }
    return "";
  };

  const {
    data: assets,
    error: assetsError,
    mutate: mutateAssets,
    isValidating: isValidatingAssets,
  } = useSWR<WithPagination<{ data: Assets[] }>, AxiosError>(
    `${assetsEndpointBase}?${
      title === "Documents"
        ? "asset_type=document"
        : title === "Digital Media" && !query.asset_type
        ? "asset_type=image&asset_type=video"
        : ""
    }${assetQueryAsParam(
      query
    )}${get_document_search_param()}&limit=${requestLimit}&show_generated_assets=true&offset=${offset}`
  );

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (isTabActive) {
      mutateAssets();
    }
  }, [isTabActive, mutateAssets]);

  const mutateCurrentPage = async () => {
    try {
      const response = await mutateAssets();
      if (response) {
        const { data, pagination } = response;
        setAssetData(data);
        setTableData(
          data.reduce<TableData>(
            (
              acc,
              {
                id,
                name,
                asset_category,
                category,
                modified_at,
                modified_by,
                is_generated,
                asset_type,
                host,
                signed_url,
                language,
                number_of_products,
                number_of_profiles,
                issue_date,
                expiration_date,
                renewal_date,
                country,
                region,
                is_cover_image,
                document_type,
                content_type,
                visibility,
                asset_mode,
                ...rest
              }
            ) => {
              acc.push({
                id,
                asset_category,
                category,
                host: {
                  id: host?.id ?? "",
                  name: host?.name ?? "",
                },
                name: name ?? "--",
                modified_at: formatDate(modified_at),
                modified_by: modified_by ?? "--",
                visibility,
                is_generated,
                asset_type,
                asset_mode,
                document_type: document_type ?? "--",
                signed_url,
                language: language?.toLocaleUpperCase(),
                issue_date: issue_date ? formatDate(issue_date) : "--",
                expiration_date: expiration_date
                  ? formatDate(expiration_date)
                  : "--",
                renewal_date: renewal_date ? formatDate(renewal_date) : "--",
                country: country ?? "--",
                country_region: `${
                  getCountryOption(countries, country)?.label ?? "--"
                } / ${region ?? "--"}`,
                region: region ?? "--",
                number_of_products: number_of_products,
                number_of_profiles: number_of_profiles ?? 0,
                info: {
                  modified_at: formatDate(modified_at),
                  modified_by: modified_by ?? "--",
                  number_of_products: number_of_products,
                  number_of_profiles: number_of_profiles ?? 0,
                  renewal_date: renewal_date ? formatDate(renewal_date) : "--",
                },
                is_cover_image,
                content_type: content_type ?? "application/pdf",
                canSelect:
                  visibility === "public" ||
                  (!roleIsGuest && visibility === "logged_in_user") ||
                  roleIsSomeKindOfSeller,
                ...rest,
              });
              return acc;
            },
            []
          )
        );
        setPagination(pagination);
      }
    } catch (error) {
      console.error("Error fetching assets:", error);
    }
  };

  const {
    data: assetFilters,
    mutate: mutateAssetFilters,
    isValidating: isValidatingAssetFilters,
  } = useSWR<
    {
      asset_category: string[];
      asset_host: string[];
      asset_type: string[];
      country: string[];
      document_type: string[];
      language: string[];
      region: string[];
    },
    AxiosError
  >(
    !hideFilters
      ? `${assetsEndpointBase}/filters?${
          title === "Documents"
            ? "asset_type=document"
            : title === "Digital Media" && !query.asset_type
            ? "asset_type=image&asset_type=video"
            : ""
        }${assetQueryAsParam(query)}${get_document_search_param()}${
          activeFilter?.length > 0 ? `&active_filter=${activeFilter}` : ``
        }`
      : null,
    {
      onError: () => {
        notifyError(t("could not load asset filters, something went wrong."));
      },
    }
  );

  const [categoryTypesFromURL, setCategoryTypesFromURL] = useState(
    getObjArrayFromStringArray(
      (title === "Documents" && query?.document_category
        ? query?.document_category
        : title === "Digital Media" && query?.media_category
        ? query?.media_category
        : []) as string[]
    )
  );

  const [mediaTypesFromURL, setMediaTypesFromURL] = useState(
    getObjArrayFromStringArray((query.asset_type ?? []) as string[])
  );

  const [docTypesFromURL, setDocTypesFromURL] = useState(
    getObjArrayFromStringArray(
      (title === "Documents"
        ? query?.document_type
        : query?.media_document_type) as string[]
    ) || []
  );

  const [countriesFromURL, setCountriesFromURLL] = useState(
    getObjArrayFromStringArray(
      (title === "Documents" && query?.document_country
        ? query?.document_country?.map(
            (country: string) =>
              countries.find((c) => c.value === country)?.label ?? country
          )
        : title === "Digital Media" && query?.media_country
        ? query?.media_country?.map(
            (country: string) =>
              countries.find((c) => c.value === country)?.label ?? country
          )
        : []) as string[]
    )
  );

  const [regionsFromURL, setRegionsFromURLL] = useState(
    getObjArrayFromStringArray(
      (title === "Documents" && query?.document_region
        ? query?.document_region
        : title === "Digital Media" && query?.media_region
        ? query?.media_region
        : []) as string[]
    )
  );
  const [languagesFromURL, setLanguagesFromURLL] = useState(
    getObjArrayFromStringArray(
      (title === "Documents" && query?.document_language
        ? query?.document_language
        : title === "Digital Media" && query?.media_language
        ? query?.media_language
        : []) as string[]
    )
  );

  const [hostsFromURL, setHostsFromURL] = useState(
    getObjArrayFromStringArray(
      (title === "Documents" && query?.document_asset_host
        ? query.document_asset_host
        : title === "Digital Media" && query?.media_asset_host
        ? query.media_asset_host
        : []) as string[]
    )
  );

  const [issue_date_from_url, set_issue_date_from_url] = useState(
    title === "Documents"
      ? query?.issue_date ?? null
      : query?.media_issue_date ?? null
  );

  const [renewal_date_from_url, set_renewal_date_from_url] = useState(
    title === "Documents"
      ? query?.renewal_date ?? null
      : query?.media_renewal_date ?? null
  );

  const [expiration_date_from_url, set_expiration_date_from_url] = useState(
    title === "Documents"
      ? query?.expiration_date ?? null
      : query?.media_expiration_date ?? null
  );

  function getObjArrayFromStringArray(strArr: string[]) {
    return strArr?.map((name) => ({ name }));
  }

  const { data: assetLanguages } = useSWR<{
    languages: Language[];
  }>(endpoints.v1_storefronts_id_languages(storefront_id), {
    onError: () => {
      notifyError(t("could not load asset languages, something went wrong."));
    },
  });

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOffset(0);
    setSearchQuery(e.target.value);
  };
  const handleClearSearch = () => {
    setSearchQuery("");
    setOffset(0);
  };

  const handleEdit = useCallback(
    (original: Assets) => {
      if (!original) {
        notifyError(t("Error loading asset data for editing"));
        return;
      }
      setSlideOut("edit");
      setEditRowData(original);
    },
    [notifyError, t]
  );

  const handleChangeRequestForm = useCallback(
    (original: Assets) => {
      if (!original) {
        notifyError(t("Error loading asset data"));
        return;
      }
      setSlideOut("change_request_form");
      setEditRowData(original);
    },
    [notifyError, t]
  );

  const can_edit_master_asset = useCallback(
    (asset: Assets) =>
      product.pim_product_type === "variant" ? !asset.is_master : true,
    [product.pim_product_type]
  );

  /**
   * When external assets are disabled, editing an externally hosted asset
   * silently resets its Asset Host to Agilis (the form hides that field and
   * submits the default host), so editing must be blocked instead.
   */
  const isExternalAssetEditDisabled = useCallback(
    (asset: Assets) =>
      !enable_external_assets && asset.asset_mode === "external",
    [enable_external_assets]
  );

  const productAssetShareUrl = useCallback(
    (asset: Assets) => {
      return `https://${window.location.hostname}/api/store/${slug}/products/${
        product.slug
      }/${asset.asset_type}/${encodeURIComponent(asset.name)}`;
    },
    [product.slug, slug]
  );

  /**
   * External assets have no stored blob for the product share endpoint to sign,
   * so their `signed_url` (the fully external URL) must be used directly.
   */
  const assetDownloadUrl = useCallback(
    (asset: Assets) =>
      useSignedAssetDownloadUrl || asset.asset_mode === "external"
        ? asset.signed_url
        : productAssetShareUrl(asset),
    [productAssetShareUrl, useSignedAssetDownloadUrl]
  );

  const handleSingleAssetShareClick = useCallback((asset: Assets) => {
    setSelectedAssetIdForSharing(asset.id);
    openShareAssetModal();
  }, []);

  const getDropdownItems = useCallback(
    (asset: Assets) => {
      const items = [
        {
          label: "Copy Link",
          icon: LinkIcon,
          onClick: () => {
            navigator.clipboard.writeText(assetDownloadUrl(asset)).then(
              () => notifySuccess(t("Asset link copied to clipboard")),
              (err) => notifyError(t("something went wrong"), err)
            );
          },
        },
      ];

      if (enable_share_assets_via_email) {
        items.push({
          label: "Share via Email",
          icon: ShareIcon,
          onClick: () => handleSingleAssetShareClick(asset),
        });
      }

      return items;
    },
    [
      enable_share_assets_via_email,
      handleSingleAssetShareClick,
      assetDownloadUrl,
      notifyError,
      notifySuccess,
      t,
    ]
  );

  const updateDropdownPosition = useCallback(
    (rowId: string) => {
      const iconEl = iconRefs.current[rowId];
      const dropdownRoot = document.getElementById(
        `dropdown-root-${convertToSlug(title)}`
      );
      const scrollContainer = scrollRef?.current;
      const dropdownHeight = 90;
      const dropdownWidth = 150;
      const iconHeight = 20;
      const verticalMargin = -18;
      const marginLeft = 10;

      if (!iconEl) return;

      const iconRect = iconEl.getBoundingClientRect();
      let top = 0;
      let left = 0;

      if (dropdownRoot && scrollContainer) {
        // Position relative to in-table dropdown root when present.
        const scrollRect = scrollContainer.getBoundingClientRect();
        const spaceBelow = scrollRect.bottom - iconRect.bottom;
        const openAbove = spaceBelow < dropdownHeight;

        top = openAbove
          ? iconRect.top -
            scrollRect.top +
            scrollContainer.scrollTop -
            dropdownHeight -
            verticalMargin
          : iconRect.top -
            scrollRect.top +
            scrollContainer.scrollTop +
            iconHeight;

        left =
          iconRect.left -
          scrollRect.left +
          scrollContainer.scrollLeft -
          dropdownWidth -
          marginLeft;
      } else {
        // Fallback for pages without scoped dropdown root; position on viewport.
        const spaceBelow = window.innerHeight - iconRect.bottom;
        const openAbove = spaceBelow < dropdownHeight;

        top = openAbove
          ? iconRect.top + window.scrollY - dropdownHeight - verticalMargin
          : iconRect.top + window.scrollY + iconHeight;
        left = iconRect.left + window.scrollX - dropdownWidth - marginLeft;
      }

      setDropdownPosition({ top, left });
    },
    [title]
  );

  const handleShareAssetViaEmail = async (
    data: IShareAssetsData
  ): Promise<void> => {
    const assetIds =
      selectedAssetIdForSharing !== null
        ? [selectedAssetIdForSharing]
        : selectedRows;

    const payload = {
      ...data,
      asset_ids: assetIds,
    };

    try {
      const response = await Axios.post(
        endpoints.v2_storefronts_storefront_id_pim_assets_send_assets(
          storefront_id
        ),
        payload
      );

      if (response?.status === 202) {
        setSelectedAssetIdForSharing(null);
        resetSelectedRowsRef.current?.resetSelectedRows();
        notifySuccess(t("Assets shared successfully"));
        mutateAssets();
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    } finally {
      closeShareAssetModal();
    }
  };

  const toggleDropDown = useCallback(
    (e: React.MouseEvent, rowId: string) => {
      e.stopPropagation();
      setOpenDropDownRowId((prev) => {
        const next = prev === rowId ? null : rowId;
        if (next) {
          updateDropdownPosition(rowId);
        }
        return next;
      });
    },
    [updateDropdownPosition]
  );

  useEffect(() => {
    if (!openDropDownRowId) return;

    const handler = () => {
      updateDropdownPosition(openDropDownRowId);
    };

    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("scroll", handler, true);
    };
  }, [openDropDownRowId, updateDropdownPosition]);

  useEffect(() => {
    if (!openDropDownRowId) return;
    const timeout_id = setTimeout(() => {
      updateDropdownPosition(openDropDownRowId);
    }, 200);
    return () => {
      clearTimeout(timeout_id);
    };
  }, [openDropDownRowId, collapsed_nav, updateDropdownPosition]);

  useEffect(() => {
    if (!openDropDownRowId) return;

    const handleClickOutside = (event: MouseEvent) => {
      const isOutside =
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !iconRefs.current[openDropDownRowId]?.contains(event.target as Node);

      if (isOutside) {
        setOpenDropDownRowId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropDownRowId]);

  const getDropdownPortalTarget = useCallback(() => {
    if (typeof document === "undefined") {
      return null;
    }
    return (
      document.getElementById(`dropdown-root-${convertToSlug(title)}`) ??
      document.body
    );
  }, [title]);

  const tableColumns = React.useMemo(
    () => [
      {
        header: t("Name"),
        accessorKey: "name",
        cell: ({
          getValue,
          row,
        }: {
          getValue: Getter<string>;
          row: {
            original: Assets;
          };
        }) => {
          const displayName = getValue() as string;
          const {
            asset_type,
            asset_mode,
            content_type,
            id,
            is_generated,
            visibility,
            signed_url,
          } = row.original;
          const idx = (tableData as Assets[]).findIndex((a) => a.id === id);
          return (
            <AssetNameIcon
              idx={idx}
              asset_type={asset_type}
              name={displayName}
              is_downloadable={
                visibility === "public" ||
                (!roleIsGuest && visibility === "logged_in_user") ||
                roleIsSomeKindOfSeller
              }
              is_generated={is_generated}
              is_external_asset={asset_mode === "external"}
              content_type={content_type}
              set_selected_asset_idx={set_selected_asset_idx}
              set_show_preview={set_show_preview}
              url={signed_url}
            />
          );
        },
      },
      {
        header: t("Category"),
        accessorKey: "asset_category",
        cell: ({
          getValue,
          row: {
            original: { visibility },
          },
        }: {
          getValue: Getter<string>;
          row: { original: Assets };
        }) => (
          <DocPortfolioName
            disabled={isOutsideLogin && visibility !== "public"}
          >
            {getValue()}
          </DocPortfolioName>
        ),
      },
      ...(enable_external_assets
        ? [
            {
              header: t("Asset Host"),
              accessorKey: "host",
              cell: ({
                getValue,
              }: {
                getValue: Getter<{ id: string; name: string }>;
              }) => {
                const host = getValue() as { id: string; name: string };
                return <span>{host?.name || "Agilis"}</span>;
              },
            },
          ]
        : []),
      {
        header: t("Language"),
        accessorKey: "language",
        cell: ({
          row: {
            original: { language, visibility },
          },
        }: {
          row: { original: Assets };
        }) => (
          <DocPortfolioName
            disabled={isOutsideLogin && visibility !== "public"}
          >
            {language?.toLocaleUpperCase()}
          </DocPortfolioName>
        ),
      },
      ...(roleIsSomeKindOfSeller &&
      (tableData.find((asset) => asset.country !== "--") ||
        tableData.find((asset) => asset.region !== "--"))
        ? [
            {
              header: t("Country / Region"),
              accessorKey: "country_region",
              enableSorting: false,
            },
          ]
        : []),
      ...(roleIsSomeKindOfSeller &&
      tableData.find((asset) => asset.issue_date !== "--")
        ? [
            {
              header: t("Issue Date"),
              accessorKey: "issue_date",
            },
          ]
        : []),
      ...(roleIsSomeKindOfSeller &&
      tableData.find((asset) => asset.expiration_date !== "--")
        ? [
            {
              header: t("Expiration Date"),
              accessorKey: "expiration_date",
            },
          ]
        : []),
      ...(roleIsSomeKindOfSeller &&
      tableData.find((asset) => asset.document_type !== "--")
        ? [
            {
              header: t("Document Type"),
              accessorKey: "document_type",
            },
          ]
        : []),
      ...(roleIsSomeKindOfSeller
        ? [
            {
              header: "",
              accessorKey: "info",
              enableSorting: false,
              align: "right",
              width: "40px",
              cell: ({
                row,
                getValue,
              }: {
                getValue: Getter<{
                  modified_at: string;
                  modified_by: string;
                  number_of_products: number;
                  number_of_profiles: number;
                  renewal_date: string;
                }>;
                row: RowType<Assets>;
              }) => {
                return (
                  <div
                    data-for={`row-tooltip-${row.original.id}`}
                    data-tip={<></>}
                    style={{ display: "inline" }}
                  >
                    <InfoIcon width={20} height={20} />
                    <ReactTooltip
                      id={`row-tooltip-${row.original.id}`}
                      place="top"
                      data-html={true}
                      effect="solid"
                      multiline={true}
                    >
                      {
                        <div style={{ textAlign: "left" }}>
                          <span
                            style={{ marginBottom: "3px", display: "block" }}
                          >
                            {t("No. of Profiles")}:{" "}
                            {getValue().number_of_profiles +
                              getValue().number_of_products}
                          </span>
                          {getValue().renewal_date !== "--" && (
                            <span
                              style={{ marginBottom: "3px", display: "block" }}
                            >
                              {t("Renewal Date")}: {getValue().renewal_date}
                            </span>
                          )}
                          {getValue().modified_at !== "--" && (
                            <span
                              style={{ marginBottom: "3px", display: "block" }}
                            >
                              {t("Last Modified On")}: {getValue().modified_at}
                            </span>
                          )}
                          {getValue().modified_by !== "--" && (
                            <span
                              style={{ marginBottom: "3px", display: "block" }}
                            >
                              {t("Last Modified By")}: {getValue().modified_by}
                            </span>
                          )}
                        </div>
                      }
                    </ReactTooltip>
                  </div>
                );
              },
            },
          ]
        : []),
      ...(isOutsideLogin
        ? []
        : [
            {
              header: productStatus !== "archived" ? t("Actions") : "",
              accessorKey: "visibility",
              cell: ({
                getValue,
                row: { original },
              }: {
                getValue: Getter<SupportedAssetAccessibility>;
                row: { original: Assets; index: number };
              }) =>
                productStatus !== "archived" &&
                !roleIsGuest && (
                  <IconWrapper>
                    {!hideShareIcon && (
                      <>
                        <BtnWrapper data-testid={`share-btn-${original.id}`}>
                          <div
                            ref={(el) => {
                              iconRefs.current[original.id] = el;
                            }}
                            onClick={(e) => toggleDropDown(e, original.id)}
                            style={{ cursor: "pointer" }}
                          >
                            <ShareIcon width={20} height={20} />
                          </div>

                          {openDropDownRowId === original.id &&
                            (() => {
                              const portalTarget = getDropdownPortalTarget();
                              if (!portalTarget) {
                                return null;
                              }
                              return createPortal(
                                <StyledDropdownMenu
                                  ref={dropdownRef}
                                  top={dropdownPosition.top}
                                  left={dropdownPosition.left}
                                >
                                  {getDropdownItems(original).map(
                                    ({ label, icon: Icon, onClick }, index) => (
                                      <DropdownItem
                                        key={index}
                                        onClick={onClick}
                                      >
                                        <Icon width={20} height={20} />
                                        <span>{label}</span>
                                      </DropdownItem>
                                    )
                                  )}
                                </StyledDropdownMenu>,
                                portalTarget
                              );
                            })()}
                        </BtnWrapper>
                        <BtnWrapper data-testid={`download-btn-${original.id}`}>
                          <div
                            ref={(el) => {
                              iconRefs.current[original.id] = el;
                            }}
                            onClick={() => {
                              window.open(assetDownloadUrl(original), "_blank");
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <DownloadIcon width={20} height={20} />
                          </div>
                        </BtnWrapper>
                      </>
                    )}

                    {assetData.length > 0 &&
                      enable_change_requests &&
                      (!hasPermission(`modify_assets`) ||
                        !hasPermission(productOrEntityPermission)) && (
                        <BtnWrapper
                          onClick={() => handleChangeRequestForm(original)}
                          type="button"
                          disabled={is_publishing_product}
                          data-testid={`change-request-btn-${original.id}`}
                        >
                          <QuoteIcon
                            fill={
                              is_publishing_product
                                ? theme.disabledButtonBG
                                : theme.primaryIconColor
                            }
                            width={20}
                            height={20}
                          />
                        </BtnWrapper>
                      )}

                    {product.is_editable &&
                      assetData.length > 0 &&
                      can_edit_master_asset(original) && (
                        <WithPermission
                          permission={[
                            "modify_assets",
                            productOrEntityPermission,
                          ]}
                          permission_type="every"
                        >
                          <div
                            data-for={`edit-disabled-tooltip-${original.id}`}
                            data-tip={
                              isExternalAssetEditDisabled(original)
                                ? t(
                                    "This asset is hosted externally and cannot be edited while external assets are disabled."
                                  )
                                : ""
                            }
                          >
                            <BtnWrapper
                              onClick={() => handleEdit(original)}
                              type="button"
                              disabled={
                                is_publishing_product ||
                                isExternalAssetEditDisabled(original)
                              }
                              data-testid={`edit-btn-${original.id}`}
                            >
                              <EditIcon
                                fill={
                                  is_publishing_product ||
                                  isExternalAssetEditDisabled(original)
                                    ? theme.disabledButtonBG
                                    : theme.primaryIconColor
                                }
                                width={20}
                                height={20}
                              />
                            </BtnWrapper>
                            <ReactTooltip
                              id={`edit-disabled-tooltip-${original.id}`}
                            />
                          </div>
                        </WithPermission>
                      )}

                    {title === "Documents" &&
                      (() => {
                        if (
                          hasPermission("modify_assets") &&
                          product.is_editable &&
                          can_edit_master_asset(original) &&
                          !hideAccessibility
                        ) {
                          return getValue() === "public" ? (
                            <>
                              <BtnWrapper
                                disabled={is_publishing_product}
                                onClick={() => handleChangeVisibility(original)}
                                data-for={`unlocked-asset-${original.id}`}
                                data-tip={t("Public")}
                                type="button"
                                data-testid={`visibility-btn-public-${original.id}`}
                              >
                                <UnLockIcon
                                  fill={
                                    is_publishing_product
                                      ? theme.disabledButtonBG
                                      : undefined
                                  }
                                  width={20}
                                  height={20}
                                />
                              </BtnWrapper>
                              <ReactTooltip
                                id={`unlocked-asset-${original.id}`}
                                effect="solid"
                              />
                            </>
                          ) : getValue() === "logged_in_user" ? (
                            <>
                              <BtnWrapper
                                disabled={is_publishing_product}
                                onClick={() => handleChangeVisibility(original)}
                                data-for={`semiLocked-asset-${original.id}`}
                                data-tip={t("Customers & Internal Users")}
                                type="button"
                                data-testid={`visibility-btn-semiLocked-${original.id}`}
                              >
                                <UnLockIcon
                                  fill={
                                    is_publishing_product
                                      ? theme.disabledButtonBG
                                      : theme.resolve("color_icon_caution")
                                  }
                                  width={20}
                                  height={20}
                                />
                              </BtnWrapper>
                              <ReactTooltip
                                id={`semiLocked-asset-${original.id}`}
                                effect="solid"
                              />
                            </>
                          ) : (
                            <>
                              <BtnWrapper
                                disabled={is_publishing_product}
                                onClick={() => handleChangeVisibility(original)}
                                data-for={`locked-asset-${original.id}`}
                                data-tip={t("Internal Users")}
                                type="button"
                                data-testid={`visibility-btn-locked-${original.id}`}
                              >
                                <LockIcon
                                  fill={
                                    is_publishing_product
                                      ? theme.disabledButtonBG
                                      : undefined
                                  }
                                  width={20}
                                  height={20}
                                />
                              </BtnWrapper>
                              <ReactTooltip
                                id={`locked-asset-${original.id}`}
                                effect="solid"
                              />
                            </>
                          );
                        } else {
                          return null;
                        }
                      })()}
                    {title === "Digital Media" &&
                      (() => {
                        if (
                          hasPermission("modify_assets") &&
                          product.is_editable &&
                          can_edit_master_asset(original)
                        ) {
                          if (getValue() === "public") {
                            return (
                              <div
                                data-tip={
                                  original.is_cover_image
                                    ? product?.cover_image?.id === original.id
                                      ? t(
                                          "You cannot hide the product cover image"
                                        )
                                      : t(
                                          "This asset is used as a cover image for another product."
                                        )
                                    : t("Public")
                                }
                                data-for={`disabled-cover-image`}
                              >
                                <BtnWrapper
                                  disabled={original.is_cover_image}
                                  onClick={() =>
                                    handleChangeVisibility(original)
                                  }
                                  data-testid={`visibility-btn-digital-public-${original.id}`}
                                >
                                  <VisibleIcon
                                    width={20}
                                    height={20}
                                    fill={
                                      original.is_cover_image
                                        ? theme.disabledLinkColor
                                        : theme.successIconColor
                                    }
                                  />
                                </BtnWrapper>
                                <ReactTooltip id={`disabled-cover-image`} />
                                <ReactTooltip
                                  id={`locked-asset-${original.id}`}
                                />
                              </div>
                            );
                          } else if (getValue() === "logged_in_user") {
                            return (
                              <div
                                data-tip={
                                  original.is_cover_image
                                    ? product?.cover_image?.id === original.id
                                      ? t(
                                          "You cannot hide the product cover image"
                                        )
                                      : t(
                                          "This asset is used as a cover image for another product."
                                        )
                                    : t("Customers & Internal Users")
                                }
                                data-for={`disabled-cover-image`}
                              >
                                <BtnWrapper
                                  disabled={original.is_cover_image}
                                  onClick={() =>
                                    handleChangeVisibility(original)
                                  }
                                  data-testid={`visibility-btn-digital-public-${original.id}`}
                                >
                                  <VisibleIcon
                                    width={20}
                                    height={20}
                                    fill={
                                      original.is_cover_image
                                        ? theme.disabledLinkColor
                                        : theme.resolve("color_icon_caution")
                                    }
                                  />
                                </BtnWrapper>
                                <ReactTooltip id={`disabled-cover-image`} />
                              </div>
                            );
                          } else {
                            return (
                              <>
                                <BtnWrapper
                                  onClick={() =>
                                    handleChangeVisibility(original)
                                  }
                                  data-for={`locked-asset-${original.id}`}
                                  data-tip={t("Internal Users")}
                                  data-testid={`visibility-btn-digital-locked-${original.id}`}
                                >
                                  <NonVisibleIcon
                                    width={20}
                                    height={20}
                                    fill={theme.errorColor}
                                  />
                                </BtnWrapper>
                                <ReactTooltip
                                  id={`locked-asset-${original.id}`}
                                />
                              </>
                            );
                          }
                        } else return null;
                      })()}
                    {product.is_editable && can_edit_master_asset(original) && (
                      <WithPermission
                        permission_type="every"
                        permission={[
                          removeAssociationPermission,
                          productOrEntityPermission,
                        ]}
                      >
                        <BtnWrapper
                          disabled={is_publishing_product}
                          onClick={() => handleDelete(original)}
                          type="button"
                          data-testid={`delete-btn-${original.id}`}
                        >
                          <DeleteIcon
                            fill={
                              is_publishing_product
                                ? theme.disabledButtonBG
                                : theme.errorColor
                            }
                          />
                        </BtnWrapper>
                      </WithPermission>
                    )}
                  </IconWrapper>
                ),
            },
          ]),
    ],
    [
      t,
      enable_external_assets,
      roleIsSomeKindOfSeller,
      tableData,
      isOutsideLogin,
      productStatus,
      roleIsGuest,
      hideShareIcon,
      openDropDownRowId,
      assetData.length,
      enable_change_requests,
      hasPermission,
      productOrEntityPermission,
      is_publishing_product,
      theme,
      product.is_editable,
      product?.cover_image?.id,
      can_edit_master_asset,
      title,
      removeAssociationPermission,
      toggleDropDown,
      getDropdownPortalTarget,
      dropdownPosition.top,
      dropdownPosition.left,
      getDropdownItems,
      assetDownloadUrl,
      handleChangeRequestForm,
      handleEdit,
      hideAccessibility,
      isExternalAssetEditDisabled,
    ]
  );

  const changeRequestContext = useMemo(
    () => ({
      variant: "asset" as const,
      assetData: {
        details: editRowData,
        attributeOptions: tableColumns
          .filter(
            ({ accessorKey }) =>
              !["info", "visibility"].includes(accessorKey as string)
          )
          .map(({ header, accessorKey }) => ({
            label: header,
            value: accessorKey,
          })),
      },
    }),
    [editRowData, tableColumns]
  );

  const onSubmitChangeRequest = async (formValues: ChangeRequestFormValues) => {
    try {
      await submitChangeRequest({
        tenant_id,
        entity: {
          id: product.id,
          name: product.name,
          entity_type: assetContainerType.split("_")[0] as EntityType,
        },
        context: changeRequestContext,
        formValues,
        isProduct: isProductContainer,
      });
      notifySuccess(t("Change request submitted successfully."));
      closeSlideOut();
    } catch (error) {
      notifyError(t("Failed to submit change request."));
    }
  };

  const handleChangeVisibility = (original: Assets) => {
    setShowEditPopup(true);
    setEditRowVisibility(original.visibility);
    setEditRowData(original);
  };

  const onComplete = async (productId?: string, shouldFetchData = false) => {
    if (productId) {
      replaceProductId(productId);
    } else if (shouldFetchData) {
      await fetchProductData();
    }
    mutateAssetFilters();
    mutateCurrentPage();
    closeSlideOut();
  };

  const associateAssetsToContainer = async (
    assetIdsToAdd: string[],
    assetIdsToRemove: string[] = []
  ) => {
    if (onAssociateAssets) {
      await onAssociateAssets(assetIdsToAdd, assetIdsToRemove);
      return;
    }

    const requestBody = isProductContainer
      ? { asset_ids: assetIdsToAdd }
      : {
          asset_ids: assetIdsToAdd,
          asset_ids_to_remove: assetIdsToRemove,
        };
    await Axios.patch(associateAssetsEndpoint, requestBody);
  };

  const handleAddExistingAssets = async ({
    selected_assets,
  }: {
    selected_assets: Assets[];
  }) => {
    await associateAssetsToContainer(selected_assets.map(({ id }) => id));
    notifySuccess(t("Assets successfully added"));
    await onComplete(undefined, true);
  };

  const handleAddNewAsset = async (data: NewAssetFormSubmitValues) => {
    if (!onAddNewAssetData) {
      return;
    }
    await onAddNewAssetData(data);
    notifySuccess(t("Asset successfully added"));
    await onComplete(undefined, true);
  };

  const handleDelete = (original: Assets) => {
    setShowDeleteModal(true);
    setDeleteRowData(original);
  };

  const methodsOfUseForm = useForm<{
    visibility: string;
    input_role_ids: string[];
    input_user_ids: string[];
    input_customer_ids: string[];
  }>({
    defaultValues: {
      visibility: "",
      input_role_ids: [],
      input_user_ids: [],
      input_customer_ids: [],
    },
  });

  const { reset, control } = methodsOfUseForm;
  const input_role_ids = useWatch({
    control,
    name: "input_role_ids",
    defaultValue: [],
  }) as string[];
  const input_user_ids = useWatch({
    control,
    name: "input_user_ids",
    defaultValue: [],
  }) as string[];
  const isSaveDisabled = useMemo(
    () =>
      disableAccessibilitySubmit(
        editRowData?.asset_type ?? "document",
        editRowVisibility || "internal",
        input_role_ids,
        input_user_ids
      ),
    [editRowVisibility, input_role_ids, input_user_ids, editRowData?.asset_type]
  );

  useEffect(() => {
    if (!editRowData) {
      reset({
        visibility: "",
        input_role_ids: [],
        input_user_ids: [],
        input_customer_ids: [],
      });
      return;
    }

    reset({
      visibility: editRowData.visibility ?? "",
      input_role_ids: editRowData.allow_all_roles
        ? ["allow_all_roles"]
        : (editRowData.roles ?? []).map((role) => role.id),
      input_user_ids: (editRowData.users ?? []).map((user) => user.id),
      input_customer_ids: [
        ...(editRowData.allow_all_customers ? ["allow_all_customers"] : []),
        ...(editRowData.allow_all_distributors
          ? ["allow_all_distributors"]
          : []),
        ...((editRowData.customers ?? []).map(
          (customer) => customer.id
        ) as string[]),
      ],
    });
  }, [editRowData, reset]);

  const handleEditConfirm = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      const assetId = editRowData ? editRowData.id : "";
      const baseURL = endpoints.v2_tenants_id_pim_assets_id(tenant_id, assetId);

      const { input_role_ids, input_user_ids, input_customer_ids } =
        methodsOfUseForm.getValues();

      const requestBody = {
        visibility: editRowVisibility,
        ...accessibilityRequestData(
          editRowData?.asset_type ?? "document",
          editRowVisibility ?? ACCESSIBILITY_TYPES.PUBLIC,
          input_customer_ids,
          input_user_ids,
          input_role_ids
        ),
        ...(!isProductContainer
          ? { entity_profile_id: resolvedAssetContainerId }
          : {}),
      };

      await Axios.patch(baseURL, requestBody);
      await fetchProductData();
      await mutateCurrentPage();
      await mutateAssetFilters();
      notifySuccess(t("Asset successfully Edited"));
      setShowEditPopup(false);
    } catch (error) {
      if (
        isAxiosError(error) &&
        (error as AxiosError)?.response?.data?.message
      ) {
        notifyError(error?.response?.data?.message);
      } else {
        notifyError(t("could not edit asset, Something went wrong."));
      }
    } finally {
      setIsSubmitting(false);
      setEditRowVisibility(null);
      setShowEditConfirm(false);
      setEditRowData(null);
    }
  };

  const handleClearFilters = () => {
    setQuery(
      title === "Documents"
        ? {
            document_category: undefined,
            document_type: undefined,
            document_country: undefined,
            document_region: undefined,
            document_language: undefined,
            asset_type: undefined,
            document_asset_host: undefined,
            issue_date: undefined,
            expiration_date: undefined,
            renewal_date: undefined,
          }
        : {
            media_category: undefined,
            media_document_type: undefined,
            asset_type: undefined,
            media_asset_host: undefined,
            media_country: undefined,
            media_region: undefined,
            media_language: undefined,
            media_issue_date: undefined,
            media_expiration_date: undefined,
            media_renewal_date: undefined,
          },
      "replaceIn"
    );
  };

  const shouldApplyPadding = () => {
    if (enable_product_asset_search) {
      if (roleIsSomeKindOfSeller) {
        return hasPermission(["view_products", "view_assets"], "every");
      }
      return true;
    }
    return roleIsSomeKindOfSeller;
  };

  useEffect(() => {
    if (debouncedSearchQuery) {
      if (title === "Documents") {
        setDocumentQ(debouncedSearchQuery);
      } else {
        setMediaQ(debouncedSearchQuery);
      }
    } else {
      if (title === "Documents") {
        setDocumentQ("");
      } else {
        setMediaQ("");
      }
    }
  }, [debouncedSearchQuery, title]);

  // Handle documentType parameter to automatically filter by document category
  useEffect(() => {
    if (documentType && title === "Documents") {
      let categoryFilter: string[] = [];

      if (documentType === "sds") {
        categoryFilter = ["Safety Data Sheet (SDS)"];
      } else if (documentType === "tds") {
        categoryFilter = ["Technical Data Sheet (TDS)"];
      }
      // For "all" or other values, don't set a category filter

      if (categoryFilter.length > 0) {
        setQuery((prev) => ({
          ...prev,
          document_category: categoryFilter,
        }));
      }
    }
  }, [documentType, title, setQuery]);

  useEffect(() => {
    const handleProductsData = (assets: Assets[]) => {
      setAssetData(assets);
      setTableData(
        assets.reduce<TableData>(
          (
            acc,
            {
              id,
              name,
              asset_category,
              category,
              host,
              modified_at,
              modified_by,
              is_generated,
              asset_type,
              signed_url,
              language,
              number_of_products,
              number_of_profiles,
              issue_date,
              expiration_date,
              renewal_date,
              country,
              region,
              is_cover_image,
              document_type,
              content_type,
              visibility,
              asset_mode,
              ...rest
            }
          ) => {
            acc.push({
              id,
              asset_category,
              category,
              host: {
                id: host?.id ?? "",
                name: host?.name ?? "",
              },
              name: name ?? "--",
              modified_at: formatDate(modified_at),
              modified_by: modified_by ?? "--",
              visibility,
              is_generated,
              asset_type,
              asset_mode,
              document_type: document_type ?? "--",
              signed_url,
              language: language?.toLocaleUpperCase(),
              issue_date: issue_date ? formatDate(issue_date) : "--",
              expiration_date: expiration_date
                ? formatDate(expiration_date)
                : "--",
              renewal_date: renewal_date ? formatDate(renewal_date) : "--",
              country: country ?? "--",
              country_region: `${
                getCountryOption(countries, country)?.label ?? "--"
              } / ${region ?? "--"}`,
              region: region ?? "--",
              number_of_products: number_of_products,
              number_of_profiles: number_of_profiles ?? 0,
              info: {
                modified_at: formatDate(modified_at),
                modified_by: modified_by ?? "--",
                number_of_products: number_of_products,
                number_of_profiles: number_of_profiles ?? 0,
                renewal_date: renewal_date ? formatDate(renewal_date) : "--",
              },
              is_cover_image,
              content_type: content_type ?? "application/pdf",
              canSelect:
                visibility === "public" ||
                (!roleIsGuest && visibility === "logged_in_user") ||
                roleIsSomeKindOfSeller,
              ...rest,
            });
            return acc;
          },
          []
        )
      );
    };
    if (assetData) {
      handleProductsData(assetData);
    }
  }, [
    assetData,
    countries,
    categoryTypesFromURL,
    languagesFromURL,
    countriesFromURL,
    mediaTypesFromURL,
    regionsFromURL,
    docTypesFromURL,
    roleIsGuest,
    roleIsSomeKindOfSeller,
    t,
    isProductContainer,
  ]);

  useEffect(() => {
    if (assetFilters) {
      setCategoryTypes(assetFilters.asset_category);

      setAssetTypes(assetFilters.asset_type);

      setAssetHostFilters(assetFilters.asset_host ?? []);

      setRegions(assetFilters.region);
      setCountryFilters(
        assetFilters.country
          .map((country) =>
            country ? getCountryOption(countries, country)?.label || "" : ""
          )
          .filter((val) => val)
      );

      setRegions(
        assetFilters.region
          .map((region) => region)
          .filter((val) => val !== "--")
      );
      setDocumentTypesFilters(
        assetFilters.document_type
          .map((document_type) => document_type)
          .filter((val) => val !== "--")
      );
    }
  }, [assetFilters, countries]);

  useEffect(() => {
    if (assetLanguages && assetFilters) {
      setLanguagesFilters(
        assetFilters?.language
          .map(
            (language) =>
              assetLanguages?.languages.find(
                (lang) => lang.alpha_2 === language?.toLowerCase()
              )?.name ?? "--"
          )
          .filter((val) => val !== "--")
      );
    }
  }, [assetFilters, assetLanguages]);

  useEffect(() => {
    setCategoryTypesFromURL(
      getObjArrayFromStringArray(
        (title === "Documents" && query?.document_category
          ? query?.document_category
          : title === "Digital Media" && query?.media_category
          ? query?.media_category
          : []) as string[]
      )
    );

    setMediaTypesFromURL(
      getObjArrayFromStringArray((query?.asset_type ?? []) as string[])
    );

    setLanguagesFromURLL(
      getObjArrayFromStringArray(
        (title === "Documents" && query?.document_language
          ? query?.document_language
          : title === "Digital Media" && query?.media_language
          ? query?.media_language
          : []) as string[]
      )
    );
    setCountriesFromURLL(
      getObjArrayFromStringArray(
        (title === "Documents" && query?.document_country
          ? query?.document_country?.map(
              (country: string) =>
                countries.find((c) => c.value === country)?.label
            )
          : title === "Digital Media" && query?.media_country
          ? query?.media_country?.map(
              (country: string) =>
                countries.find((c) => c.value === country)?.label
            )
          : []
        )?.filter((t: string) => t !== undefined) as string[]
      )
    );
    setDocTypesFromURL(
      getObjArrayFromStringArray(
        (title === "Documents" && query?.document_type
          ? query?.document_type
          : title === "Digital Media" && query?.media_document_type
          ? query?.media_document_type
          : []) as string[]
      )
    );
    setRegionsFromURLL(
      getObjArrayFromStringArray(
        (title === "Documents" && query?.document_region
          ? query?.document_region
          : title === "Digital Media" && query?.media_region
          ? query?.media_region
          : []) as string[]
      )
    );
    setHostsFromURL(
      getObjArrayFromStringArray(
        (title === "Documents" && query?.document_asset_host
          ? query.document_asset_host
          : title === "Digital Media" && query?.media_asset_host
          ? query.media_asset_host
          : []) as string[]
      )
    );
    set_issue_date_from_url(
      title === "Documents"
        ? query.issue_date ?? null
        : query.media_issue_date ?? null
    );
    set_expiration_date_from_url(
      title === "Documents"
        ? query.expiration_date ?? null
        : query.media_expiration_date ?? null
    );
    set_renewal_date_from_url(
      title === "Documents"
        ? query.renewal_date ?? null
        : query.media_renewal_date ?? null
    );
  }, [query, title, countries]);

  const handleCategoryType = ({
    values,
  }: {
    values: ChipType[];
    filter: string;
  }) => {
    setQuery(
      title === "Documents"
        ? { document_category: chipsToStringArray(values) }
        : { media_category: chipsToStringArray(values) },
      "replaceIn"
    );
    setActiveFilter("asset_category");
  };

  const handleMediaType = ({
    values,
  }: {
    values: ChipType[];
    filter: string;
  }) => {
    setQuery({ asset_type: chipsToStringArray(values) }, "replaceIn");
    setActiveFilter("asset_type");
  };

  const handleCountryFilter = ({ values }: { values: ChipType[] }) => {
    let countryList = chipsToStringArray(values).map(
      (country) => countries.find((c) => c.label === country)?.value ?? country
    );
    setQuery(
      title === "Documents"
        ? {
            document_country: countryList,
          }
        : {
            media_country: countryList,
          },
      "replaceIn"
    );
    setActiveFilter("country");
  };

  const handleRegionFilter = ({ values }: { values: ChipType[] }) => {
    let regions = chipsToStringArray(values);
    setQuery(
      title === "Documents"
        ? { document_region: regions }
        : { media_region: regions },
      "replaceIn"
    );
    setActiveFilter("region");
  };

  const handleLanguageFilter = ({ values }: { values: ChipType[] }) => {
    let languages = chipsToStringArray(
      values.map((val) => ({
        name:
          assetLanguages?.languages.find(
            (languages) => languages.name.indexOf(val.name) === 0
          )?.alpha_2 || val.name,
      }))
    );
    setQuery(
      title === "Documents"
        ? { document_language: languages }
        : { media_language: languages },
      "replaceIn"
    );
    setActiveFilter("language");
  };

  const handleDocTypeFilter = ({ values }: { values: ChipType[] }) => {
    let docTypes = chipsToStringArray(values);
    setQuery(
      title === "Documents"
        ? { document_type: docTypes }
        : { media_document_type: docTypes },
      "replaceIn"
    );
    setActiveFilter("document_type");
  };

  const handleHostFilter = ({ values }: { values: ChipType[] }) => {
    setQuery(
      title === "Documents"
        ? { document_asset_host: chipsToStringArray(values) }
        : { media_asset_host: chipsToStringArray(values) },
      "replaceIn"
    );
    setActiveFilter("asset_host");
  };

  const handleIssueDateFilter = ({ values }: { values: DateRange }) => {
    const updated_values = {
      startDate: values?.startDate?.toISOString(),
      endDate: values?.endDate?.endOf("day").toISOString(),
    };
    setQuery(
      title === "Documents"
        ? { issue_date: updated_values }
        : { media_issue_date: updated_values },
      "replaceIn"
    );
    setActiveFilter("issue_date");
  };

  const handleExpirationDateFilter = ({ values }: { values: DateRange }) => {
    const updated_values = {
      startDate: values?.startDate?.toISOString(),
      endDate: values?.endDate?.endOf("day").toISOString(),
    };
    setQuery(
      title === "Documents"
        ? { expiration_date: updated_values }
        : { media_expiration_date: updated_values },
      "replaceIn"
    );
    setActiveFilter("expiration_date");
  };

  const handleRenewalDateFilter = ({ values }: { values: DateRange }) => {
    const updated_values = {
      startDate: values?.startDate?.toISOString(),
      endDate: values?.endDate?.endOf("day").toISOString(),
    };
    setQuery(
      title === "Documents"
        ? { renewal_date: updated_values }
        : { media_renewal_date: updated_values },
      "replaceIn"
    );
    setActiveFilter("renewal_date");
  };

  useEffect(() => {
    if (assets?.data) {
      if (offset === 0) {
        setAssetData(assets?.data);
      } else {
        setAssetData((prev) => [...prev, ...assets?.data]);
      }
      setPagination(assets.pagination);
      setIsLoadingData(false);
    }
  }, [assets, offset]);

  useEffect(() => {
    setOffset(0);
  }, [query, document_q, media_q]);

  const onScroll = () => {
    if (scrollRef.current) {
      const scrollTop = scrollRef.current.scrollTop;
      if (
        scrollRef.current.offsetHeight - scrollTop < 200 &&
        !isLoadingData &&
        ((pagination?.total && assetData.length < pagination?.total) ||
          !pagination) &&
        !isValidatingAssets
      ) {
        setIsLoadingData(true);
        setOffset(offset + requestLimit);
      }
    }
  };

  useEffect(() => {
    if (selectedRows && selectedRows !== "all" && assetData.length) {
      const selected_rows_obj = selectedRows.reduce<Record<string, boolean>>(
        (acc, id) => ({ ...acc, [id]: true }),
        {}
      );
      setSelectedItems(
        !selectedRows.length
          ? []
          : assetData.filter((asset) => selected_rows_obj[asset.id])
      );
    }
  }, [assetData, selectedRows]);

  const checkReverseMatch = (
    assets: Assets[],
    generatedAssets: ReturnType<typeof getActiveTemplateGeneratedAssets>
  ): boolean => {
    if (generatedAssets.length === 0 || !Array.isArray(assets)) return false;

    const formattedCategories = generatedAssets.map((category) => ({
      label: category.asset_category || "",
      value: category.category_id || "",
    }));

    const assetCategoryIds = new Set(
      assets
        .filter((asset) => asset.is_generated)
        .map((asset) => asset.category?.id)
    );

    const missingIds = formattedCategories
      .map((item) => item.value)
      .filter((value) => !assetCategoryIds.has(value));

    return missingIds.length === 0;
  };

  const allDynamicCategoriesExistInAssets = checkReverseMatch(
    assetData,
    getActiveTemplateGeneratedAssets(templateGeneratedAssets)
  );

  const mainSection = (
    <>
      <TableFiltersWrapper
        has_padding={shouldApplyPadding()}
        style={{ position: "relative" }}
      >
        {(enable_product_asset_search || showSearch) && (
          <>
            {roleIsSomeKindOfSeller ? (
              <WithPermission
                permission_type="every"
                permission={["view_assets", "view_products"]}
              >
                <Row style={{ margin: `${spacingTokens.spacing_lg} 0` }}>
                  <SearchBar
                    query={searchQuery}
                    placeHolder={t("Search by Asset Name")}
                    handleChange={handleSearch}
                    handleClearInput={handleClearSearch}
                    maxWidth="450px"
                  />
                </Row>
              </WithPermission>
            ) : (
              <Row style={{ margin: `${spacingTokens.spacing_lg} 0` }}>
                <SearchBar
                  query={searchQuery}
                  placeHolder={t("Search by Asset Name")}
                  handleChange={handleSearch}
                  handleClearInput={handleClearSearch}
                  maxWidth="450px"
                />
              </Row>
            )}
          </>
        )}

        {showAddButtonAtTop && (
          <div
            style={{
              display: "flex",
              gap: spacingTokens.spacing_xl,
              position: "absolute",
              right: "20px",
              top: "19px",
            }}
          >
            <PrimaryButtonMediumV2
              style={{ width: "fit-content" }}
              onClick={() => setSlideOut("add")}
              type="button"
            >
              <PlusIcon />
              {t("Add Document")}
            </PrimaryButtonMediumV2>
          </div>
        )}

        {roleIsSomeKindOfSeller && !hideFilters && (
          <div
            style={{
              display: "flex",
              gap: theme.resolve("spacing_xs"),
              flexWrap: "wrap",
            }}
          >
            {title === "Digital Media" && assetTypes?.length > 0 && (
              <FilterBy
                setter={handleMediaType}
                filter={"asset_type"}
                options={getObjArrayFromStringArray(assetTypes)}
                header={t("Type")}
                label={t("Type")}
                preselectedFilters={
                  isChipTypeArray(mediaTypesFromURL) ? mediaTypesFromURL : []
                }
              />
            )}
            {categoryTypes?.length > 0 && (
              <FilterBy
                setter={handleCategoryType}
                filter={
                  title === "Documents" ? "document_category" : "media_category"
                }
                options={getObjArrayFromStringArray(categoryTypes)}
                header={t("Category")}
                label={t("Category")}
                preselectedFilters={
                  isChipTypeArray(categoryTypesFromURL)
                    ? categoryTypesFromURL
                    : []
                }
              />
            )}
            {assetLanguages && languagesFilters.length > 0 && (
              <FilterBy
                setter={handleLanguageFilter}
                filter="language"
                options={getObjArrayFromStringArray(languagesFilters)}
                header={t("Language")}
                label={t("Language")}
                preselectedFilters={
                  isChipTypeArray(languagesFromURL)
                    ? languagesFromURL.map((lang) => ({
                        name:
                          assetLanguages.languages.find(
                            (assetLang) => assetLang.alpha_2 === lang.name
                          )?.name ?? lang.name,
                      }))
                    : []
                }
              />
            )}
            {countryFilters.length > 0 && (
              <FilterBy
                setter={handleCountryFilter}
                filter="country"
                options={getObjArrayFromStringArray(countryFilters)}
                header={t("Country")}
                label={t("Country")}
                preselectedFilters={
                  isChipTypeArray(countriesFromURL) ? countriesFromURL : []
                }
              />
            )}
            {regions?.length > 0 && (
              <FilterBy
                setter={handleRegionFilter}
                filter="region"
                options={regions.map((region) => ({ name: region }))}
                header={t("Region")}
                label={t("Region")}
                preselectedFilters={
                  isChipTypeArray(regionsFromURL) ? regionsFromURL : []
                }
              />
            )}

            {documentTypesFilters?.length > 0 && (
              <FilterBy
                setter={handleDocTypeFilter}
                filter="document_type"
                options={getObjArrayFromStringArray(documentTypesFilters)}
                header={t("Document Type")}
                label={t("Document Type")}
                preselectedFilters={
                  isChipTypeArray(docTypesFromURL) ? docTypesFromURL : []
                }
              />
            )}
            {enable_external_assets && assetHostFilters.length > 0 && (
              <FilterBy
                setter={handleHostFilter}
                filter="asset_host"
                options={getObjArrayFromStringArray(assetHostFilters)}
                header={t("Asset Host")}
                label={t("Asset Host")}
                preselectedFilters={
                  isChipTypeArray(hostsFromURL) ? hostsFromURL : []
                }
              />
            )}
            <DateFilter
              setter={handleIssueDateFilter}
              filter="issue_date"
              label={t("Issue Date")}
              preselectedDateRange={get_preselected_date_range(
                issue_date_from_url as DateRangeStr
              )}
            />
            <DateFilter
              setter={handleExpirationDateFilter}
              filter="expiration_date"
              label={t("Expiration Date")}
              preselectedDateRange={get_preselected_date_range(
                expiration_date_from_url as DateRangeStr
              )}
            />
            <DateFilter
              setter={handleRenewalDateFilter}
              filter="renewal_date"
              label={t("Renewal Date")}
              preselectedDateRange={get_preselected_date_range(
                renewal_date_from_url as DateRangeStr
              )}
            />
            {assetQueryAsParam(query).length > 0 && (
              <TertiaryTextButtonSmall
                onClick={() => handleClearFilters()}
                style={{ marginBottom: 0 }}
              >
                {t("Clear Filters")}
              </TertiaryTextButtonSmall>
            )}
          </div>
        )}
      </TableFiltersWrapper>
      <TableContainer>
        {list_grid_toggle === "list" ? (
          <>
            {isShowTableWithBulkAction ? (
              <TableWithBulkAction
                columns={tableColumns}
                data={tableData}
                isLoading={isLoadingData}
                error={assetsError}
                lastChildleftAlign
                Placeholder={<TablePlaceholder />}
                handleSelectRows={(rows) => setSelectedRows(rows)}
                defaultSelectedRows={selectedRows}
                bulkActionElement={({ rows, resetSelectedRows }) => {
                  return (
                    <BulkActionOnAssets
                      rows={rows}
                      resetSelectedRows={resetSelectedRows}
                      tenant_id={tenant_id}
                      fetchSelectedAssets={() =>
                        fetchSelectedAssets({
                          rows,
                          selectedItems,
                          tableData: tableData as Assets[],
                        })
                      }
                      mutateAssets={mutateAssets}
                      canDeleteAssets={false}
                      onOpenShareAssetModal={openShareAssetModal}
                      showShareViaEmail={enable_share_assets_via_email}
                    />
                  );
                }}
                onScroll={onScroll}
                scrollRef={scrollRef}
                bulkSelectLimit={500}
                portalId={`dropdown-root-${convertToSlug(title)}`}
                ref={resetSelectedRowsRef}
              />
            ) : (
              <ScrollTableWrapper ref={scrollRef} onScroll={onScroll}>
                <Table
                  columns={tableColumns}
                  data={tableData}
                  isLoading={isLoadingData}
                  error={assetsError}
                  lastChildleftAlign
                  Placeholder={<TablePlaceholder />}
                />
              </ScrollTableWrapper>
            )}
            {show_preview && (
              <DocumentPreview
                assets={
                  [
                    ...tableData.map((asset) => ({
                      ...(asset as Assets),
                      can_edit:
                        !isOutsideLogin &&
                        hasPermission("modify_assets") &&
                        productStatus !== "archived" &&
                        product.is_editable &&
                        can_edit_master_asset(asset as Assets) &&
                        !isExternalAssetEditDisabled(asset as Assets),
                      can_delete:
                        !isOutsideLogin &&
                        hasPermission(removeAssociationPermission) &&
                        productStatus !== "archived" &&
                        product.is_editable &&
                        can_edit_master_asset(asset as Assets),
                      can_change_visibility: false,
                      can_external_download: true,
                      can_view_details: !isOutsideLogin,
                    })),
                  ] as (Assets & GridItemProps)[]
                }
                selected_asset_idx={selected_asset_idx}
                onCancelPreview={() => set_show_preview(false)}
                onEdit={(asset) => handleEdit(asset)}
                onDownload={(asset) =>
                  window.open(assetDownloadUrl(asset), "_blank")
                }
                is_product_page={true}
              />
            )}
          </>
        ) : (
          <ScrollGridWrapper ref={scrollRef} onScroll={onScroll}>
            {isShowGridAssetWithBulkAction ? (
              <GridAssetViewWithBulkAction
                assets={
                  assetData.map((asset) => ({
                    ...asset,
                    can_edit:
                      !isOutsideLogin &&
                      hasPermission("modify_assets") &&
                      productStatus !== "archived" &&
                      product.is_editable &&
                      can_edit_master_asset(asset) &&
                      !isExternalAssetEditDisabled(asset),
                    can_change_visibility:
                      !isOutsideLogin &&
                      hasPermission("modify_assets") &&
                      productStatus !== "archived" &&
                      product.is_editable &&
                      can_edit_master_asset(asset),
                    can_delete:
                      !isOutsideLogin &&
                      hasPermission(removeAssociationPermission) &&
                      productStatus !== "archived" &&
                      product.is_editable &&
                      can_edit_master_asset(asset),
                    can_external_download:
                      asset.visibility === "public" ||
                      (!roleIsGuest && asset.visibility === "logged_in_user") ||
                      roleIsSomeKindOfSeller,
                    can_view_details: !isOutsideLogin,
                  })) as (Assets & GridItemProps)[]
                }
                handle_asset_visibility_change={handleChangeVisibility}
                on_edit_asset={handleEdit}
                on_remove_asset={handleDelete}
                on_download_asset={(asset) => {
                  window.open(assetDownloadUrl(asset), "_blank");
                }}
                handleSelectedItems={(items) =>
                  setSelectedRows(items.map((item) => item.original.id))
                }
                selectedItems={selectedItems.map(
                  (item) =>
                    ({ original: item, id: `${item.id}` } as TableRow<Assets>)
                )}
                bulkActionElement={({ rows, resetSelectedRows }) => {
                  return (
                    <BulkActionOnAssets
                      rows={rows}
                      resetSelectedRows={resetSelectedRows}
                      canDeleteAssets={false}
                      tenant_id={tenant_id}
                      mutateAssets={mutateAssets}
                      fetchSelectedAssets={() =>
                        fetchSelectedAssets({
                          rows,
                          selectedItems,
                          tableData: tableData as Assets[],
                        })
                      }
                      onOpenShareAssetModal={openShareAssetModal}
                      showShareViaEmail={enable_share_assets_via_email}
                    />
                  );
                }}
                bulkSelectLimit={500}
                handleSingleAssetShare={
                  enable_share_assets_via_email
                    ? handleSingleAssetShareClick
                    : undefined
                }
                productSlug={product.slug}
              />
            ) : (
              <GridAssetView
                assets={
                  assetData.map((asset) => ({
                    ...asset,
                    can_edit:
                      !isOutsideLogin &&
                      hasPermission("modify_assets") &&
                      productStatus !== "archived" &&
                      product.is_editable &&
                      can_edit_master_asset(asset) &&
                      !isExternalAssetEditDisabled(asset),
                    can_change_visibility:
                      !isOutsideLogin &&
                      hasPermission("modify_assets") &&
                      productStatus !== "archived" &&
                      product.is_editable &&
                      can_edit_master_asset(asset),
                    can_delete:
                      !isOutsideLogin &&
                      hasPermission(removeAssociationPermission) &&
                      productStatus !== "archived" &&
                      product.is_editable &&
                      can_edit_master_asset(asset),
                    can_external_download:
                      asset.visibility === "public" ||
                      (!roleIsGuest && asset.visibility === "logged_in_user") ||
                      roleIsSomeKindOfSeller,
                    can_view_details: !isOutsideLogin,
                  })) as (Assets & GridItemProps)[]
                }
                handle_asset_visibility_change={handleChangeVisibility}
                on_edit_asset={handleEdit}
                on_remove_asset={handleDelete}
                on_download_asset={(asset) => {
                  window.open(assetDownloadUrl(asset), "_blank");
                }}
                productSlug={product.slug}
              />
            )}
          </ScrollGridWrapper>
        )}
      </TableContainer>

      {hasPermission("modify_assets") &&
        productStatus !== "archived" &&
        product.is_editable &&
        !hideAddButton && (
          <div style={{ display: "flex", gap: spacingTokens.spacing_xl }}>
            <TertiaryButtonMediumV2
              style={{ width: "fit-content" }}
              onClick={() => setSlideOut("add")}
              type="button"
            >
              <PlusIcon />
              {t("Add")}
            </TertiaryButtonMediumV2>
            {isProductContainer &&
              allowGenerateDocuments &&
              title === "Documents" &&
              !allDynamicCategoriesExistInAssets && (
                <PrimaryButtonMediumV2
                  style={{ width: "fit-content" }}
                  onClick={() => setSlideOut("generate")}
                >
                  <PlusIcon />
                  {t("Generate Document")}
                </PrimaryButtonMediumV2>
              )}
          </div>
        )}
    </>
  );

  return isOutsideLogin &&
    tableData.length === 0 &&
    get_document_search_param() === "" ? (
    <></>
  ) : (
    <div className={PROFILE_DETAIL_CLASSES.assetsTable}>
      {!hideHeader ? (
        <AccordionCard
          title={title === "Documents" ? t("Documents") : t("Digital Media")}
          subtitle={
            isOutsideLogin
              ? undefined
              : t(`Manage {{title}} and control their accessibility`, {
                  title,
                })
          }
          expanded={true}
          actionChildren={() => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: spacingTokens.spacing_md,
              }}
            >
              {(isValidatingAssetFilters || isValidatingAssets) && (
                <div>
                  <LoadingIcon width={22} height={22} />
                </div>
              )}
              {!hideViewToggle && (
                <ListGridToggleWrapper>
                  <ListGridToggleButton
                    set_toggle_state={set_list_grid_toggle}
                    name={`${title}${
                      isOutsideLogin ? "_portfolio" : ""
                    }_list_grid_toggle`}
                  />
                </ListGridToggleWrapper>
              )}
            </div>
          )}
        >
          {mainSection}
        </AccordionCard>
      ) : (
        mainSection
      )}

      {showSlideOut === "add" && (
        <SlideOut
          title={
            title === "Documents" ? t("Add Documents") : t("Add Digital Media")
          }
          closeFlyout={closeSlideOut}
          show={!!showSlideOut}
        >
          <AddAssetsForm
            title={title}
            onComplete={onComplete}
            product={product}
            assetContainerType={assetContainerType}
            fetchProductData={mutateAssets}
            onCancel={closeSlideOut}
            uploadNewDocumentsOnly={uploadNewDocumentsOnly}
            hideAccessibility={hideAccessibility}
            allowUpload={allowUpload}
            onAddExistingAssets={
              !isProductContainer || onAssociateAssets
                ? handleAddExistingAssets
                : undefined
            }
            onAddNewAssetData={
              onAddNewAssetData ? handleAddNewAsset : undefined
            }
          />
        </SlideOut>
      )}

      {showSlideOut === "edit" && (
        <SlideOut
          closeFlyout={async () => {
            closeSlideOut();
            product_document_ref.current?.clear_poll_timeout();
            await fetchProductData();
            await mutateCurrentPage();
            await mutateAssetFilters();
          }}
          show={!!showSlideOut}
          title={
            editRowData?.is_generated
              ? t("Edit Generated Document")
              : title === "Documents"
              ? t("Edit Document")
              : t("Edit Digital Media")
          }
        >
          {editRowData?.is_generated ? (
            <EditOrGenerateProductDocument
              asset={{
                ...editRowData,
                language: getLanguageOptionFromSupportedLanguage(
                  editRowData!.language,
                  supported_languages
                ).value,
              }}
              assetContainerType={assetContainerType}
              ref={product_document_ref}
              onSuccess={onComplete}
              onCancel={closeSlideOut}
              product={product}
              isCategoryDisabled={true}
            />
          ) : editRowData ? (
            <EditAssetForm
              onComplete={onComplete}
              fetchProductData={fetchProductData}
              product={product}
              assetContainerType={assetContainerType}
              assetContainerId={resolvedAssetContainerId}
              assetData={editRowData}
              formType={title}
              onCancel={closeSlideOut}
              hideAccessibility={hideAccessibility}
              allowUpload={allowUpload}
              enableSave={enableSave}
              isProductContext={isProductContainer}
            />
          ) : (
            <ErrorPlaceholder
              message={t(
                "Error: Asset data is missing. This should not happen. Please refresh the page and try again."
              )}
            />
          )}
        </SlideOut>
      )}
      {showSlideOut === "generate" && (
        <SlideOut
          closeFlyout={async () => {
            closeSlideOut();
            product_document_ref.current?.clear_poll_timeout();
            await mutateAssets();
            await mutateAssetFilters();
          }}
          show={!!showSlideOut}
          title={t("Generate Document")}
        >
          <EditOrGenerateProductDocument
            product={product}
            ref={product_document_ref}
            replaceProductId={replaceProductId}
            onSuccess={onComplete}
            onCancel={closeSlideOut}
            assetContainerType={assetContainerType}
          />
        </SlideOut>
      )}
      {showSlideOut === "change_request_form" && (
        <ChangeRequestFormModal
          open={Boolean(showSlideOut === "change_request_form")}
          onClose={() => {
            closeSlideOut();
          }}
          assigneePermissions={[productOrEntityPermission]}
          variant={changeRequestContext.variant}
          assetData={changeRequestContext.assetData}
          onSubmit={onSubmitChangeRequest}
          submitting={isSubmitting}
        />
      )}
      {showEditPopup && (
        <>
          <Modal
            overlay
            show={showEditPopup}
            closeModal={() => {
              setShowEditPopup(false);
              setShowEditConfirm(false);
              setEditRowData(null);
            }}
          >
            <H3 style={{ margin: `10px ${spacingTokens.spacing_none}` }}>
              {t("Edit Asset Accessibility")}
            </H3>
            {!showEditConfirm && (
              <>
                <div style={{ marginBottom: "30px" }}>
                  <SmallText>
                    {editRowData?.asset_type === "document"
                      ? t("Document")
                      : t("Media")}{" "}
                    {t("can be downloaded by:")}
                  </SmallText>
                </div>
                {editRowData?.asset_type !== "document" ? (
                  <>
                    <div
                      style={{
                        marginBottom: "15px",
                        marginTop: spacingTokens.spacing_2xl,
                      }}
                    >
                      <RadioButton
                        name={"public"}
                        value="public"
                        checked={editRowVisibility === "public"}
                        disabled={isRestricted}
                        optionTitle={t("Public")}
                        handleChange={() => setEditRowVisibility("public")}
                      />
                    </div>
                    <div style={{ marginBottom: "15px" }}>
                      <RadioButton
                        name={"logged_in_user"}
                        value="logged_in_user"
                        checked={editRowVisibility === "logged_in_user"}
                        disabled={isRestricted}
                        optionTitle={t("Customers & Internal Users")}
                        handleChange={() =>
                          setEditRowVisibility("logged_in_user")
                        }
                      />
                    </div>
                    <div style={{ marginBottom: "15px" }}>
                      <RadioButton
                        name={"internal"}
                        value="internal"
                        checked={editRowVisibility === "internal"}
                        optionTitle={t("Internal Users Only")}
                        handleChange={() => setEditRowVisibility("internal")}
                      />
                    </div>
                    <PrimaryButtonLargeV2
                      loading={isSubmitting}
                      style={{ width: "100%" }}
                      onClick={() => setShowEditConfirm(true)}
                    >
                      {t("Save Changes")}
                    </PrimaryButtonLargeV2>
                  </>
                ) : (
                  <>
                    <div style={{ marginBottom: "15px" }}>
                      <DocumentAccessibility
                        accessibility={editRowVisibility || "internal"}
                        methodsOfUseForm={methodsOfUseForm}
                        onAccessibilityChange={(newAccessibility) =>
                          setEditRowVisibility(
                            newAccessibility as SupportedAssetAccessibility
                          )
                        }
                        assetContainerType={assetContainerType}
                        existingData={{
                          users: getUsersExistingData(editRowData?.users),
                          roles: getRolesExistingData(
                            editRowData?.allow_all_roles,
                            editRowData?.roles
                          ),
                          customers: getCustomersExistingData(
                            editRowData?.allow_all_customers,
                            editRowData?.allow_all_distributors,
                            editRowData?.customers
                          ),
                        }}
                      />
                    </div>
                    <Flex
                      style={{
                        columnGap: "16px",
                      }}
                    >
                      <TertiaryButtonMediumV2
                        style={{ width: "100%" }}
                        onClick={() => {
                          setShowEditPopup(false);
                          setShowEditConfirm(false);
                          setEditRowData(null);
                        }}
                      >
                        {t("Cancel")}
                      </TertiaryButtonMediumV2>
                      <PrimaryButtonLargeV2
                        loading={isSubmitting}
                        style={{ width: "100%" }}
                        onClick={() => setShowEditConfirm(true)}
                        disabled={isSaveDisabled}
                      >
                        {t("Save")}
                      </PrimaryButtonLargeV2>
                    </Flex>
                  </>
                )}
              </>
            )}
            {showEditConfirm && (
              <>
                <ModalWrapper>
                  <div style={{ textAlign: "center" }}>
                    {editRowData?.visibility === "public" &&
                    editRowData.number_of_attribute_values > 0
                      ? t(
                          "This asset will become inaccessible to users outside login. Would you like to continue?"
                        )
                      : t(
                          " This change will affect {{number_of_profiles}} other profiles. Would you like to continue?",
                          {
                            number_of_profiles:
                              (editRowData?.number_of_profiles ?? 0) +
                              (editRowData?.number_of_products ?? 0),
                          }
                        )}
                  </div>
                </ModalWrapper>
                <Flex
                  style={{
                    columnGap: spacingTokens.spacing_xl,
                  }}
                >
                  <TertiaryButtonMediumV2
                    style={{ width: "100%" }}
                    onClick={() => {
                      setShowEditPopup(false);
                      setShowEditConfirm(false);
                    }}
                  >
                    {t("Cancel")}
                  </TertiaryButtonMediumV2>
                  <PrimaryButtonMediumV2
                    loading={isSubmitting}
                    style={{ width: "100%" }}
                    onClick={handleEditConfirm}
                  >
                    {t("Confirm")}
                  </PrimaryButtonMediumV2>
                </Flex>
              </>
            )}
          </Modal>
        </>
      )}

      {showDeleteModal && (
        <RemoveAssetFromProduct
          onComplete={(id) => {
            onComplete(id);
            closeDeleteModal();
          }}
          product={product}
          show={showDeleteModal}
          onClose={closeDeleteModal}
          refreshAssetsList={mutateAssets}
          assetData={deleteRowData}
          onRemoveAsset={onRemoveAssociatedAsset}
          confirmationBody={removeConfirmationBody}
        />
      )}

      {enable_share_assets_via_email && (
        <SlideOut
          title={t("Share Asset")}
          show={showShareAssetModal}
          closeFlyout={closeShareAssetModal}
        >
          <ShareAsset
            handleShareAssetViaEmail={handleShareAssetViaEmail}
            closeModal={closeShareAssetModal}
          />
        </SlideOut>
      )}
    </div>
  );
};
